/**
 * Resolve buyer / address fields for sales order resolve payload (create + edit).
 */
import {
  addressOwnerIdFromSelection,
  buildOrderPartyIdentityPayload,
  identityFromSelection,
  type PartyIdentity,
} from "../../utils/orderPartyIdentity";

export type PickSalesResolveBuyerInput = {
  values: {
    userType?: string;
    isSameAsBilling?: boolean;
    userFirstName?: string;
    userLastName?: string;
    userEmail?: string;
    userPhone?: string;
    teamMemberId?: string;
    teamMemberName?: string;
  };
  selectedOfficeAgent?: {
    value?: string;
    membershipId?: string;
    globalUserId?: string;
    organizationId?: string;
    firstName?: string;
    lastName?: string;
    label?: string;
  } | null;
  selectedAgent?: {
    value?: string;
    membershipId?: string;
    globalUserId?: string;
    organizationId?: string;
    firstName?: string;
    lastName?: string;
    label?: string;
    brokerageFirmName?: string;
    brokerageFirmOrgId?: string;
    organization_id?: string;
  } | null;
  selectedOffice?: { value?: string; label?: string } | null;
  offices?: Array<{ _id?: string; officeAddress?: unknown[] }> | null;
  selectedBrokerageFirm?: {
    value?: string;
    label?: string;
    membershipId?: string;
    organizationId?: string;
    organization_id?: string;
  } | null;
  /** Fallback when teamMemberId is not yet in form values (matches buildSalesOrder payload builder). */
  selectedTeamMemberOption?: {
    value?: string;
    membershipId?: string;
    globalUserId?: string;
    organizationId?: string;
  } | null;
  selectedBillingAddress?: unknown;
  selectedShippingAddress?: unknown;
  selectedOfficeBillingAddress?: unknown;
  selectedOfficeShippingAddress?: unknown;
};

export type SalesResolveBuyerContext = {
  customerId: string;
  agentId: string | null;
  agentName: string | null;
  brokerageFirmId: string | null;
  brokerageFirmName: string | null;
  office: unknown;
  inventorySource: string;
  billingAddress: unknown;
  shippingAddress: unknown;
  buyerType: string;
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerPhone: string;
  teamMemberId: string | null;
  teamMemberName: string;
  buyer: PartyIdentity;
  agent: PartyIdentity | null;
  teamMember: PartyIdentity | null;
  brokerageFirmOrgId: string | null;
  addressOwnerId: string;
};

function partyFromOption(
  opt: Record<string, unknown> | null | undefined
): PartyIdentity | null {
  const id = identityFromSelection(opt);
  return id.membershipId ? id : null;
}

export function pickSalesResolveBuyerContext(
  input: PickSalesResolveBuyerInput
): SalesResolveBuyerContext {
  const { values } = input;
  let customerId = "";
  let agentId: string | null = null;
  let agentName: string | null = null;
  let brokerageFirmId: string | null = null;
  let office: unknown = null;
  let inventorySource = "agent";
  // Prefer personal selection, fall back to office bucket so resolve never drops a UI-selected address.
  let billingAddress =
    input.selectedBillingAddress || input.selectedOfficeBillingAddress || null;
  let shippingAddress =
    values.isSameAsBilling && billingAddress
      ? billingAddress
      : (input.selectedShippingAddress ||
        input.selectedOfficeShippingAddress ||
        null);

  let agentParty: PartyIdentity | null = null;
  let teamMemberParty: PartyIdentity | null = partyFromOption(
    input.selectedTeamMemberOption as Record<string, unknown> | undefined
  );

  if (input.selectedOfficeAgent) {
    agentParty = partyFromOption(input.selectedOfficeAgent as Record<string, unknown>);
    agentId = agentParty?.membershipId ?? input.selectedOfficeAgent.value ?? null;
    const first = input.selectedOfficeAgent.firstName ?? "";
    const last = input.selectedOfficeAgent.lastName ?? "";
    agentName = (input.selectedAgent?.label ?? `${first} ${last}`).trim();
    customerId = String(agentParty?.membershipId ?? input.selectedOfficeAgent.value ?? "");
    brokerageFirmId = input.selectedBrokerageFirm?.value ?? null;
  } else if (input.selectedAgent) {
    agentParty = partyFromOption(input.selectedAgent as Record<string, unknown>);
    agentId = agentParty?.membershipId ?? input.selectedAgent.value ?? null;
    const first = input.selectedAgent.firstName ?? "";
    const last = input.selectedAgent.lastName ?? "";
    agentName = (input.selectedAgent.label ?? `${first} ${last}`).trim();
    customerId = String(agentParty?.membershipId ?? input.selectedAgent.value ?? "");
  } else if (input.selectedOffice) {
    const selectedOfficeData = input.offices?.find((o) => o._id === input.selectedOffice?.value);
    const primaryAddress = selectedOfficeData?.officeAddress?.[0];
    office = {
      _id: input.selectedOffice.value,
      officeName: input.selectedOffice.label,
      officeType: input.selectedOffice.label,
      officeAddress: primaryAddress,
      status: true,
    };
    customerId = String(input.selectedBrokerageFirm?.value ?? "");
    brokerageFirmId = input.selectedBrokerageFirm?.value ?? null;
    inventorySource = "office";
    agentId = null;
    agentParty = null;
    billingAddress =
      input.selectedOfficeBillingAddress || input.selectedBillingAddress || null;
    shippingAddress =
      values.isSameAsBilling && billingAddress
        ? billingAddress
        : (input.selectedOfficeShippingAddress ||
          input.selectedShippingAddress ||
          null);
  }

  const effectiveTeamMemberId = String(
    values.teamMemberId ?? input.selectedTeamMemberOption?.value ?? ""
  ).trim();
  if (effectiveTeamMemberId && !teamMemberParty) {
    teamMemberParty = {
      membershipId: effectiveTeamMemberId,
      globalUserId: String(input.selectedTeamMemberOption?.globalUserId ?? "").trim(),
      organizationId: String(
        input.selectedOffice?.value ??
          input.selectedTeamMemberOption?.organizationId ??
          ""
      ).trim(),
    };
  }

  if (input.selectedOfficeAgent?.value) {
    customerId = String(
      agentParty?.membershipId ?? input.selectedOfficeAgent.value
    );
  } else if (input.selectedAgent?.value) {
    customerId = String(agentParty?.membershipId ?? input.selectedAgent.value);
  } else if (effectiveTeamMemberId) {
    customerId = effectiveTeamMemberId;
  } else if (!customerId && input.selectedBrokerageFirm?.value) {
    customerId = String(input.selectedBrokerageFirm.value);
  }

  const brokerageFirmOrgId =
    input.selectedBrokerageFirm?.organizationId ??
    input.selectedBrokerageFirm?.organization_id ??
    input.selectedBrokerageFirm?.value ??
    input.selectedAgent?.brokerageFirmOrgId ??
    (input.selectedAgent as { brokerage_firm_organization_id?: string } | null | undefined)
      ?.brokerage_firm_organization_id ??
    null;

  if (brokerageFirmOrgId) {
    brokerageFirmId = String(brokerageFirmOrgId);
  }

  const buyerMembershipId = customerId;
  const buyer: PartyIdentity = {
    membershipId: buyerMembershipId,
    globalUserId:
      agentParty?.globalUserId ??
      teamMemberParty?.globalUserId ??
      identityFromSelection(input.selectedBrokerageFirm as Record<string, unknown>).globalUserId,
    organizationId:
      agentParty?.organizationId ??
      teamMemberParty?.organizationId ??
      identityFromSelection(input.selectedBrokerageFirm as Record<string, unknown>).organizationId,
  };

  const buyerType = String(values.userType ?? "").trim().toLowerCase();
  let addressOwnerId = "";
  if ((buyerType === "teammember" || buyerType === "office") && input.selectedOffice?.value) {
    addressOwnerId = String(input.selectedOffice.value);
  } else if (buyerType === "agent") {
    addressOwnerId = String(
      agentParty?.organizationId ||
        input.selectedAgent?.organizationId ||
        input.selectedAgent?.organization_id ||
        ""
    );
  } else {
    addressOwnerId =
      addressOwnerIdFromSelection(input.selectedOfficeAgent as Record<string, unknown>) ||
      addressOwnerIdFromSelection(input.selectedAgent as Record<string, unknown>) ||
      addressOwnerIdFromSelection(input.selectedTeamMemberOption as Record<string, unknown>) ||
      addressOwnerIdFromSelection(input.selectedBrokerageFirm as Record<string, unknown>) ||
      buyerMembershipId;
  }

  return {
    customerId: buyerMembershipId,
    agentId,
    agentName,
    brokerageFirmId,
    brokerageFirmName:
      input.selectedBrokerageFirm?.label ??
      input.selectedAgent?.brokerageFirmName ??
      null,
    office,
    inventorySource,
    billingAddress,
    shippingAddress,
    buyerType: String(values.userType ?? ""),
    customerFirstName: String(values.userFirstName ?? ""),
    customerLastName: String(values.userLastName ?? ""),
    customerEmail: String(values.userEmail ?? ""),
    customerPhone: String(values.userPhone ?? ""),
    teamMemberId: values.teamMemberId ? String(values.teamMemberId) : null,
    teamMemberName: String(values.teamMemberName ?? ""),
    buyer,
    agent: agentParty,
    teamMember: teamMemberParty,
    brokerageFirmOrgId: brokerageFirmOrgId ? String(brokerageFirmOrgId) : null,
    addressOwnerId,
  };
}

export function salesResolvePartyPayloadFields(
  buyer: SalesResolveBuyerContext,
  seller: PartyIdentity
) {
  return buildOrderPartyIdentityPayload({
    seller,
    buyer: buyer.buyer.membershipId ? buyer.buyer : { membershipId: buyer.customerId, globalUserId: "", organizationId: "" },
    agent: buyer.agent,
    teamMember: buyer.teamMember,
    brokerageFirmOrgId: buyer.brokerageFirmOrgId,
  });
}
